export type RichTextEditorVariant = 'email-fax' | 'sms-notes'

export const RICH_TEXT_EDITOR_VARIANTS: RichTextEditorVariant[] = [
  'email-fax',
  'sms-notes',
]

export const RICH_TEXT_EDITOR_VARIANT_LABELS: Record<
  RichTextEditorVariant,
  string
> = {
  'email-fax': 'Email & Fax',
  'sms-notes': 'SMS & Notes',
}

export type RichTextEditorToolbarItemId =
  | 'undo'
  | 'redo'
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'fontSize'
  | 'alignment'
  | 'divider'
  | 'bulletList'
  | 'orderedList'
  | 'blockquote'
  | 'indentLeft'
  | 'indentRight'
  | 'highlight'
  | 'variable'
  | 'ai'
  | 'link'
  | 'textColor'
  | 'bgColor'

export type RichTextEditorBodyActionId = 'template' | 'refine' | 'variables'

export type RichTextEditorFooterActionId = 'magic' | 'emoji' | 'attach' | 'send'

export type RichTextEditorToolbarItem =
  | RichTextEditorToolbarItemId
  | RichTextEditorToolbarItemId[]

export type RichTextEditorToolbarGroup = RichTextEditorToolbarItem[]

export const RICH_TEXT_EDITOR_FONT_SIZE_OPTIONS = [
  '10 pt',
  '12 pt',
  '14 pt',
  '16 pt',
  '18 pt',
] as const

export type RichTextEditorFontSize =
  (typeof RICH_TEXT_EDITOR_FONT_SIZE_OPTIONS)[number]

export const DEFAULT_EMAIL_FAX_TOOLBAR: RichTextEditorToolbarGroup[] = [
  ['undo', 'redo', 'bold', 'italic', 'underline', 'strikethrough', 'textColor'],
  [
    'fontSize',
    'bgColor',
    'alignment',
    'bulletList',
    'orderedList',
    'blockquote',
    'indentLeft',
    'indentRight',
    'divider',
    'variable',
    'link',
  ],
]

/** SMS & Notes: Bold–Strikethrough, List, Link (no undo/redo). */
export const DEFAULT_SMS_NOTES_TOOLBAR: RichTextEditorToolbarGroup[] = [
  ['bold', 'italic', 'underline', 'strikethrough', 'bulletList', 'link'],
]

/**
 * SMS & Notes toolbar with the Variable button. Used automatically when the
 * `variables` prop is passed and no `toolbarGroups` override is given.
 */
export const DEFAULT_SMS_NOTES_TOOLBAR_WITH_VARIABLES: RichTextEditorToolbarGroup[] =
  [
    [
      'bold',
      'italic',
      'underline',
      'strikethrough',
      'bulletList',
      'variable',
      'link',
    ],
  ]

/**
 * The current design drops the body action chips — AI is reached through the
 * toolbar button, text selection, or the footer magic button instead. All three
 * chips remain supported and can be restored by passing `bodyActions`.
 */
export const DEFAULT_BODY_ACTIONS: Record<
  RichTextEditorVariant,
  RichTextEditorBodyActionId[]
> = {
  'email-fax': [],
  'sms-notes': [],
}

export const DEFAULT_FOOTER_ACTIONS: RichTextEditorFooterActionId[] = [
  'magic',
  'emoji',
  'attach',
  'send',
]

export const RICH_TEXT_EDITOR_TOOLBAR_ITEM_LABELS: Record<
  RichTextEditorToolbarItemId,
  string
> = {
  undo: 'Undo',
  redo: 'Redo',
  bold: 'Bold',
  italic: 'Italic',
  underline: 'Underline',
  strikethrough: 'Strikethrough',
  fontSize: 'Font size',
  alignment: 'Alignment',
  divider: 'Divider',
  bulletList: 'Bullet list',
  orderedList: 'Numbered list',
  blockquote: 'Blockquote',
  indentLeft: 'Decrease indent',
  indentRight: 'Increase indent',
  highlight: 'Highlight',
  variable: 'Insert variable',
  ai: 'AI assist',
  link: 'Insert link',
  textColor: 'Text color',
  bgColor: 'Background color',
}

export type RichTextEditorVariable = {
  id: string
  /** Text shown in the variable menu. */
  label: string
  /** Text inserted into the editor when picked. Defaults to label. */
  value?: string
}

export type RichTextEditorAttachment = {
  /** Derived from name, size, and mtime so re-picking a file is a no-op. */
  id: string
  file: File
  name: string
  sizeLabel: string
}

export type RichTextEditorColorOption = {
  id: string
  label: string
  value: string
}

export const RICH_TEXT_EDITOR_TEXT_COLOR_OPTIONS: RichTextEditorColorOption[] =
  [
    { id: 'default', label: 'Default', value: '#1f1f1f' },
    { id: 'blue', label: 'Blue', value: '#1865d8' },
    { id: 'indigo', label: 'Indigo', value: '#5e69e8' },
    { id: 'teal', label: 'Teal', value: '#00977b' },
    { id: 'green', label: 'Green', value: '#009124' },
    { id: 'orange', label: 'Orange', value: '#e76e00' },
    { id: 'red', label: 'Red', value: '#a8253a' },
    { id: 'purple', label: 'Purple', value: '#865ade' },
  ]

export const RICH_TEXT_EDITOR_BG_COLOR_OPTIONS: RichTextEditorColorOption[] = [
  { id: 'none', label: 'No fill', value: 'transparent' },
  { id: 'yellow', label: 'Yellow', value: '#fdf3d8' },
  { id: 'lime', label: 'Lime', value: '#ebf5d1' },
  { id: 'green', label: 'Green', value: '#e2fce2' },
  { id: 'cyan', label: 'Cyan', value: '#ddf8fe' },
  { id: 'blue', label: 'Blue', value: '#eaf5fe' },
  { id: 'indigo', label: 'Indigo', value: '#e0e9ff' },
  { id: 'pink', label: 'Pink', value: '#ffe5f7' },
]

export type RichTextEditorAiActionId =
  | 'fixGrammar'
  | 'improveWriting'
  | 'moreConcise'
  | 'moreDetailed'
  | 'summarize'
  | 'changeTone'

export type RichTextEditorAiToneId =
  | 'formal'
  | 'casual'
  | 'friendly'
  | 'neutral'
  | 'simple'

export type RichTextEditorAiResultActionId =
  | 'regenerate'
  | 'replace'
  | 'insert'
  | 'copy'
  | 'cancel'

export const RICH_TEXT_EDITOR_AI_ACTIONS: RichTextEditorAiActionId[] = [
  'fixGrammar',
  'improveWriting',
  'moreConcise',
  'moreDetailed',
  'summarize',
  'changeTone',
]

export const RICH_TEXT_EDITOR_AI_TONES: RichTextEditorAiToneId[] = [
  'formal',
  'casual',
  'friendly',
  'neutral',
  'simple',
]

export const RICH_TEXT_EDITOR_AI_RESULT_ACTIONS: RichTextEditorAiResultActionId[] =
  ['regenerate', 'replace', 'insert', 'copy', 'cancel']

export const RICH_TEXT_EDITOR_AI_ACTION_LABELS: Record<
  RichTextEditorAiActionId,
  string
> = {
  fixGrammar: 'Fix grammar & spelling',
  improveWriting: 'Improve writing',
  moreConcise: 'More concise',
  moreDetailed: 'More detailed',
  summarize: 'Summarize',
  changeTone: 'Change tone',
}

export const RICH_TEXT_EDITOR_AI_TONE_LABELS: Record<
  RichTextEditorAiToneId,
  string
> = {
  formal: 'Formal',
  casual: 'Casual',
  friendly: 'Friendly',
  neutral: 'Neutral',
  simple: 'Simple',
}

export const RICH_TEXT_EDITOR_AI_RESULT_ACTION_LABELS: Record<
  RichTextEditorAiResultActionId,
  string
> = {
  regenerate: 'Regenerate text',
  replace: 'Replace generated text',
  insert: 'Insert text',
  copy: 'Copy',
  cancel: 'Cancel',
}

export const RICH_TEXT_EDITOR_BODY_ACTION_LABELS: Record<
  RichTextEditorBodyActionId,
  string
> = {
  template: 'Template',
  refine: 'Refine',
  variables: 'Variables',
}

export const RICH_TEXT_EDITOR_FOOTER_ACTION_LABELS: Record<
  RichTextEditorFooterActionId,
  string
> = {
  magic: 'Magic assist',
  emoji: 'Emoji',
  attach: 'Attach',
  send: 'Send',
}

export type RichTextEditorProps = {
  variant?: RichTextEditorVariant
  value?: string
  defaultValue?: string
  placeholder?: string
  disabled?: boolean
  /** Dark theme surface. */
  darkMode?: boolean
  className?: string
  toolbarGroups?: RichTextEditorToolbarGroup[]
  bodyActions?: RichTextEditorBodyActionId[]
  footerActions?: RichTextEditorFooterActionId[]
  fontSizeOptions?: readonly RichTextEditorFontSize[]
  /**
   * Fires on every edit with the sanitized HTML and the matching plain text,
   * so hosts can store or validate either one.
   */
  onChange?: (value: string, plainText: string) => void
  /**
   * Variables offered by the toolbar's Variable button. The sms-notes toolbar
   * only shows the button when this list is non-empty.
   */
  variables?: RichTextEditorVariable[]
  /**
   * Fires after a variable is picked. The editor has already inserted it at the
   * caret (in --ds-blue-500); use this to track it or run follow-up logic.
   */
  onVariableSelect?: (variable: RichTextEditorVariable) => void
  onSend?: () => void
  onToolbarAction?: (actionId: RichTextEditorToolbarItemId) => void
  onBodyAction?: (actionId: RichTextEditorBodyActionId) => void
  onFooterAction?: (actionId: RichTextEditorFooterActionId) => void
  /** Renders the toolbar row on its own, with no body or footer. */
  toolbarOnly?: boolean
  /**
   * Figma "Toolbar only" variant: gives the toolbar row its own border and
   * surface and moves the footer actions inline to the end of the row. Leave
   * off to embed a bare toolbar inside a host-provided bar.
   */
  toolbarActions?: boolean
  onFontSizeChange?: (fontSize: RichTextEditorFontSize) => void
  onAlignmentChange?: (
    alignment: 'left' | 'center' | 'right' | 'justify',
  ) => void
  textColorOptions?: RichTextEditorColorOption[]
  backgroundColorOptions?: RichTextEditorColorOption[]
  onTextColorChange?: (color: RichTextEditorColorOption) => void
  onBackgroundColorChange?: (color: RichTextEditorColorOption) => void
  /**
   * Fires with the picked emoji. The editor inserts it at the caret on its own;
   * this is for hosts that render the toolbar without a body (`toolbarOnly`).
   */
  onEmojiSelect?: (emoji: string) => void
  /** Fires with the files newly chosen from the attach button. */
  onAttach?: (files: File[]) => void
  /** Fires with the full attachment list after any add or remove. */
  onAttachmentsChange?: (files: File[]) => void
  /** Forwarded to the hidden file input, e.g. 'image/*,.pdf'. */
  attachAccept?: string
  attachMultiple?: boolean
  /**
   * Turn off when the host renders its own attachment list, so files are only
   * reported through the callbacks.
   */
  showAttachments?: boolean
  /** Opens the AI assist menu when text is selected inside the editor. */
  aiEnabled?: boolean
  /**
   * Generated copy to preview. When set, the AI popover swaps from the assist
   * menu to the result menu. The component never calls an AI service itself.
   */
  aiResult?: string | null
  onAiAction?: (actionId: RichTextEditorAiActionId) => void
  onAiToneSelect?: (toneId: RichTextEditorAiToneId) => void
  onAiResultAction?: (
    actionId: RichTextEditorAiResultActionId,
    result: string,
  ) => void
}
